import styled from "styled-components"

import type { EngagementKpiMetric, EngagementPeriodLabels, TrendDirection } from "../MonthlyEngagementPage/types"
import { TrendSparkline } from "./TrendSparkline"

const TrendMetricCardContainer = styled.div`
  position: relative;
  min-height: 116px;
  padding: 14px 18px;
  background: var(--surface-1);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  overflow: hidden;
`

const TrendMetricTitle = styled.div`
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`

const TrendMetricSubtitle = styled.div`
  margin-top: 2px;
  color: var(--text-muted);
  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
`

const TrendMetricValue = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 18px;
  color: var(--text-secondary);
  font-size: 24px;
  font-weight: 500;
  line-height: 30px;
`

const TrendMetricText = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
`

const TrendMetricIcon = styled.span<{ $direction: TrendDirection }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: ${({ $direction }) => ($direction === "down" ? "#ef4444" : "#22c55e")};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  ${({ $direction }) =>
    $direction === "down" &&
    `
      transform: rotate(180deg);
      transform-origin: center;
    `}
`

const TrendMetricPercent = styled.span<{ $direction: TrendDirection }>`
  color: ${({ $direction }) => {
    if ($direction === "down") return "#ef4444"
    if ($direction === "flat") return "#64748b"
    return "#22c55e"
  }};
`

const TrendMetricPeriod = styled.span`
  color: var(--text-muted);
`

const getDisplayPercent = (value?: number) => Math.abs(value ?? 0)

interface TrendMetricCardProps {
  metric: EngagementKpiMetric
  periodLabels: EngagementPeriodLabels
}

export const TrendMetricCard = ({ metric, periodLabels }: TrendMetricCardProps) => (
  <TrendMetricCardContainer>
    <TrendMetricTitle>{metric.title}</TrendMetricTitle>
    <TrendMetricSubtitle>{metric.subtitle}</TrendMetricSubtitle>
    <TrendMetricValue>{metric.value}</TrendMetricValue>
    <TrendMetricText>
      {metric.direction === "up" || metric.direction === "down" ? (
        <TrendMetricIcon $direction={metric.direction}>
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M8 13V3" />
            <path d="M4.5 6.5L8 3l3.5 3.5" />
          </svg>
        </TrendMetricIcon>
      ) : null}
      <TrendMetricPercent $direction={metric.direction}>{getDisplayPercent(metric.percent_change)}%</TrendMetricPercent>
      <TrendMetricPeriod>{periodLabels.past}</TrendMetricPeriod>
    </TrendMetricText>
    <TrendSparkline values={metric.sparkline} direction={metric.direction} />
  </TrendMetricCardContainer>
)
